/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useErrorState } from '../../shared/useErrorState';
import { VStack, Box } from 'chakra-v3';
import type { UserVideoComponent } from '@karya/core';
import type { ComponentRendererProps } from '../../shared/Types';
import { getTypedParams, getTypedComponent } from '../../shared/Types';
import { Text } from '../../../../../components/Text';
import { ChatBubble } from '../../shared/ChatBubble';
import { Button } from '../../../../../components/buttons/Index';
import {
  MediaDeviceError,
  MinRecordingLengthError,
  MaxRecordingLengthError,
  RecordingError,
  FileSizeExceededError,
} from './ErrorMessages';
import { useNext } from '../../../hooks/useNext';

export const UserVideo: React.FC<ComponentRendererProps> = ({ node, isCurrent, controller }) => {
  const params = getTypedParams<UserVideoComponent>(node);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [duration, setDuration] = useState<number>(0);
  const [error, setError] = useErrorState();
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const videoChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const startTimeRef = useRef<number>(0);
  const validationPassedRef = useRef<boolean>(true);

  const prompt = params.prompt;
  const minRecordingLength = params.minRecordingLength;
  const maxRecordingLength = params.maxRecordingLength;

  const getErrorMessage = (error: unknown): Error => {
    return new MediaDeviceError(error, 'both');
  };

  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    // Capture recordingUrl and videoRef at effect time (videoRef is React-managed)
    const currentRecordingUrl = recordingUrl;
    const currentVideo = videoRef.current;

    return () => {
      // Access refs directly inside cleanup to get latest values (our own refs)
      const currentMediaRecorder = mediaRecorderRef.current;
      const currentStream = streamRef.current;

      // Stop media recorder if still recording
      if (currentMediaRecorder && currentMediaRecorder.state !== 'inactive') {
        try {
          currentMediaRecorder.stop();
        } catch (error) {
          console.error('Error stopping media recorder on cleanup:', error);
        }
      }

      // Stop all media stream tracks
      if (currentStream) {
        currentStream.getTracks().forEach((track) => {
          track.stop();
          currentStream.removeTrack(track);
        });
        streamRef.current = null;
      }

      // Revoke object URLs to prevent memory leaks
      if (currentRecordingUrl) {
        URL.revokeObjectURL(currentRecordingUrl);
      }

      // Clean up video element
      if (currentVideo) {
        currentVideo.srcObject = null;
      }
    };
  }, [recordingUrl]);

  const teardownFn = useCallback(
    (next: () => void) => {
      if (!recordingUrl) {
        return;
      }

      // Final validation before submission
      if (minRecordingLength !== undefined && duration < minRecordingLength) {
        setError(() => new MinRecordingLengthError(minRecordingLength, duration));
        return;
      }
      if (maxRecordingLength !== undefined && duration > maxRecordingLength) {
        setError(() => new MaxRecordingLengthError(maxRecordingLength, duration));
        return;
      }
      // Validate file size (reasonable default: 100MB for video)
      const videoBlob =
        videoChunksRef.current.length > 0 ? new Blob(videoChunksRef.current, { type: 'video/webm' }) : null;
      if (videoBlob) {
        const maxFileSize = 100 * 1024 * 1024; // 100MB
        if (videoBlob.size > maxFileSize) {
          const fileSizeMB = videoBlob.size / (1024 * 1024);
          setError(() => new FileSizeExceededError(fileSizeMB, 100));
          return;
        }
      }

      // Write outputs - video and duration are output params for UserVideoComponent
      const component = getTypedComponent<UserVideoComponent>(node);
      controller.writeOutput(component.video, recordingUrl);
      if (component.duration) {
        controller.writeOutput(component.duration, duration);
      }

      // Only call next() when validation passes and output is written
      next();
    },
    [recordingUrl, minRecordingLength, maxRecordingLength, duration, node, controller, setError],
  );

  useNext({
    controller,
    teardownFn,
    strategy: 'MANUAL',
    isCurrent,
  });

  // Ensure video element receives the stream when it becomes available or when recording state changes
  useEffect(() => {
    if (videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [isRecording]);

  // Real-time duration update while recording
  useEffect(() => {
    if (!isRecording) {
      return;
    }

    const intervalId = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setDuration(elapsed);

      // Auto-stop when maxRecordingLength is reached
      if (maxRecordingLength !== undefined && elapsed >= maxRecordingLength) {
        // Clear interval first to prevent multiple calls
        clearInterval(intervalId);
        // Call stopRecording to trigger cleanup
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          validationPassedRef.current = true;
          mediaRecorderRef.current.stop();
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
          }
          setIsRecording(false);
          setDuration(maxRecordingLength);
        }
      }
    }, 500); // Update every 500ms for smooth UI updates

    return () => {
      clearInterval(intervalId);
    };
  }, [isRecording, maxRecordingLength]);

  const startRecording = async () => {
    try {
      setError(null);
      validationPassedRef.current = true; // Reset validation flag

      // Clean up any existing stream before creating a new one
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      // Revoke old recording URL if it exists
      if (recordingUrl) {
        URL.revokeObjectURL(recordingUrl);
        setRecordingUrl(null);
      }

      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      videoChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          videoChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Only create URL if validation passed
        if (!validationPassedRef.current) {
          return;
        }
        const videoBlob = new Blob(videoChunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(videoBlob);
        setRecordingUrl(url);
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        setError(() => new RecordingError());
        setIsRecording(false);
        // Clean up stream on error
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      };

      mediaRecorder.start();
      setDuration(0);
      startTimeRef.current = Date.now();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      setError(() => getErrorMessage(error));
      // Clean up stream if it was created before error
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      validationPassedRef.current = true; // Reset validation flag
      mediaRecorderRef.current.stop();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      setIsRecording(false);
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setDuration(elapsed);

      // Validate duration limits
      if (minRecordingLength !== undefined && elapsed < minRecordingLength) {
        validationPassedRef.current = false;
        setError(() => new MinRecordingLengthError(minRecordingLength, elapsed));
        // Revoke URL if it was created before validation failed
        if (recordingUrl) {
          URL.revokeObjectURL(recordingUrl);
        }
        setRecordingUrl(null);
        return;
      }
      if (maxRecordingLength !== undefined && elapsed > maxRecordingLength) {
        validationPassedRef.current = false;
        setError(() => new MaxRecordingLengthError(maxRecordingLength, elapsed));
        // Revoke URL if it was created before validation failed
        if (recordingUrl) {
          URL.revokeObjectURL(recordingUrl);
        }
        setRecordingUrl(null);
        return;
      }
    }
  };

  return (
    <ChatBubble
      containerProps={{ w: 'fit-content', minW: { base: '200px', md: '300px' }, maxW: '70vw', alignSelf: 'flex-end' }}
    >
      <VStack gap="16px" align="stretch">
        {prompt && <Text variant="body">{prompt}</Text>}
        {error && (
          <Text variant="body" color="error.solid">
            {error.message}
          </Text>
        )}
        {!recordingUrl && (
          <VStack gap="12px" align="stretch">
            {isRecording && (
              <Box w="100%" maxH="400px" bg="black">
                <video ref={videoRef} autoPlay muted playsInline style={{ width: '100%', maxHeight: '400px' }} />
              </Box>
            )}
            {!isRecording && (
              <Button
                onClick={() => {
                  void startRecording();
                }}
                variant="secondary"
                disabled={!isCurrent}
              >
                Start Recording
              </Button>
            )}
            {isRecording && (
              <>
                <Text variant="body">
                  Recording... {duration}s
                  {minRecordingLength && duration < minRecordingLength && (
                    <Text as="span" color="error.solid">
                      {' '}
                      (min: {minRecordingLength}s)
                    </Text>
                  )}
                  {maxRecordingLength && duration >= maxRecordingLength && (
                    <Text as="span" color="error.solid">
                      {' '}
                      (max: {maxRecordingLength}s reached)
                    </Text>
                  )}
                </Text>
                <Button
                  onClick={stopRecording}
                  variant="secondary"
                  disabled={minRecordingLength !== undefined && duration < minRecordingLength}
                >
                  {minRecordingLength !== undefined && duration < minRecordingLength
                    ? `Record at least ${minRecordingLength}s`
                    : 'Stop Recording'}
                </Button>
              </>
            )}
          </VStack>
        )}
        {recordingUrl && !isRecording && (
          <VStack gap="12px">
            <Box w="100%" maxH="400px">
              <video src={recordingUrl} controls style={{ width: '100%', maxHeight: '400px' }} />
            </Box>
            <Text variant="caption">Duration: {duration}s</Text>
          </VStack>
        )}
      </VStack>
    </ChatBubble>
  );
};
